import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { addNotification } from "@/shared/states/notification";
import { UploadedFile, UploadProgress } from "@/shared/types/file-upload";
import { AxiosProgressEvent } from "axios";
import { CircleHelp } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ThirdPartyVendorLayout } from ".";
import { useMarkQuestionnaireCompleted, useSubmitVendorIntakeForm } from "../requests";
import { ChatMessage } from "../types";
import SimpleTPEChat from "./components/SimpleTPEChat";
import SubmitConfirmationModal from "./components/SubmitConfirmationModal";
import WelcomeModal from "./components/WelcomeModal";

interface SuccessScreenProps {
  token: string;
  chatMessages?: ChatMessage[];
  isLoadingMessages?: boolean;
  sessionId?: string;
  onMarkCompleted?: () => void;
  onUserMessageSent?: (message: ChatMessage) => void;
  areAllQuestionsAnswered?: boolean;
  assessorName?: string;
  onViewPreviousResponses?: () => void;
  hasChatWelcomeShownRef?: React.MutableRefObject<boolean>;
}

const SuccessScreen: React.FC<SuccessScreenProps> = ({
  token,
  chatMessages = [],
  isLoadingMessages = false,
  sessionId,
  onMarkCompleted,
  onUserMessageSent,
  areAllQuestionsAnswered: propAreAllQuestionsAnswered,
  assessorName,
  onViewPreviousResponses,
  hasChatWelcomeShownRef
}) => {
  const [uploadedChatFiles, setUploadedChatFiles] = useState<UploadedFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showChatWelcomeModal, setShowChatWelcomeModal] = useState(false);
  // Default to false (show modal) until we get actual data from API or socket
  const [areAllQuestionsAnswered, setAreAllQuestionsAnswered] = useState(propAreAllQuestionsAnswered ?? false);
  // Track progress percentage from chat - start at -1 to indicate not yet loaded
  const [progressPercentage, setProgressPercentage] = useState(-1);

  // Use local ref as fallback if parent ref is not provided
  const localHasChatWelcomeShownRef = useRef(false);
  const effectiveRef = hasChatWelcomeShownRef || localHasChatWelcomeShownRef;

  // Show chat welcome modal on first load when messages are ready and progress is 0%
  useEffect(() => {
    // Only show modal when we have actual progress data (>= 0) and it's exactly 0%
    if (!isLoadingMessages && chatMessages.length > 0 && progressPercentage === 0 && !effectiveRef.current) {
      setShowChatWelcomeModal(true);
      effectiveRef.current = true;
    }
  }, [isLoadingMessages, chatMessages.length, progressPercentage, effectiveRef]);

  // Sync local state when prop changes (e.g., when API data loads)
  useEffect(() => {
    if (propAreAllQuestionsAnswered !== undefined) {
      setAreAllQuestionsAnswered(propAreAllQuestionsAnswered);
    }
  }, [propAreAllQuestionsAnswered]);
  
  const { mutate: submitVendorDetails } = useSubmitVendorIntakeForm();
  const { mutate: markCompleted, isPending: isMarkingCompleted } = useMarkQuestionnaireCompleted();

  // Chat file upload handlers
  const handleChatFileUpload = useCallback(async (files: File[]) => {
    const MAX_FILE_SIZE_MB = 20;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    const MAX_FILE_COUNT = 10;
    
    let newUploadedFiles: UploadedFile[] = [];
    
    // Add files to state and calculate validation inside the setter to use fresh state
    setUploadedChatFiles(prev => {
      // Count files that are successfully uploaded or currently uploading (not errors, not failed)
      const currentValidCount = prev.filter(
        f => f.status === 'success' || f.status === 'gettingurl' || f.status === 'inprogress'
      ).length;
      
      // Get existing file names for duplicate check
      const existingFileNames = new Set(
        prev.map(f => `${f.file.name}_${f.file.size}_${f.file.lastModified}`)
      );
      
      newUploadedFiles = files.map((file, index) => {
        const currentPosition = currentValidCount + index + 1;
        let status: 'gettingurl' | 'error' = 'gettingurl';
        let errorMessage: string | undefined;
        
        // Create unique file identifier
        const fileIdentifier = `${file.name}_${file.size}_${file.lastModified}`;
        
        // Check for duplicate files
        if (existingFileNames.has(fileIdentifier)) {
          status = 'error';
          errorMessage = "Duplicate file";
        }
        // Check if exceeds file count limit
        else if (currentPosition > MAX_FILE_COUNT) {
          status = 'error';
          errorMessage = "Maximum 10 files allowed";
        }
        // Check if exceeds size limit
        else if (file.size > MAX_FILE_SIZE_BYTES) {
          status = 'error';
          errorMessage = `File exceeds 20MB limit (${(file.size / (1024 * 1024)).toFixed(1)}MB)`;
        }
        
        return {
          id: uuidv4(),
          file: file,
          preview: URL.createObjectURL(file),
          status,
          progressBar: status === 'gettingurl',
          signedUrl: undefined,
          errorMessage,
        };
      });
      
      return [...prev, ...newUploadedFiles];
    });
    
    // Process each file for upload (skip error files)
    for (const uploadedFile of newUploadedFiles) {
      // Skip files with errors
      if (uploadedFile.status === 'error') {
        continue;
      }
      
      try {
        // Get signed URL
        const signedUrls = await getSignedUrl({
          file_names: [uploadedFile.file.name],
          sec_ctx: 'tpra_chat'
        });
        
        if (signedUrls && signedUrls.length > 0) {
          const signedUrl = signedUrls[0];
          
          // Update status to inprogress
          setUploadedChatFiles(prev => 
            prev.map(f => f.id === uploadedFile.id ? { ...f, status: 'inprogress' as const, signedUrl } : f)
          );
          
          // Initialize progress
          setUploadProgress(prev => ({
            ...prev,
            [uploadedFile.file.name]: 0
          }));
          
          // Upload file with progress tracking
          await uploadFileReq(signedUrl, uploadedFile.file, (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(prev => ({
                ...prev,
                [uploadedFile.file.name]: progress
              }));
            }
          });
          
          // Clear progress and update status to success
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[uploadedFile.file.name];
            return newProgress;
          });
          
          setUploadedChatFiles(prev => 
            prev.map(f => f.id === uploadedFile.id ? 
              { ...f, status: 'success' as const, signedUrl } : f
            )
          );
        } else {
          throw new Error('Failed to get signed URL');
        }
      } catch (error) {
        console.error('File upload error:', error);
        
        // Clear progress
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[uploadedFile.file.name];
          return newProgress;
        });
        
        // Update status to failed
        setUploadedChatFiles(prev => 
          prev.map(f => f.id === uploadedFile.id ? { ...f, status: 'failed' as const } : f)
        );
        
        addNotification({
          type: 'error',
          message: `Failed to upload ${uploadedFile.file.name}`,
        });
      }
    }
  }, []);

  const handleRemoveChatFile = useCallback(async (fileId: string) => {
    const MAX_FILE_COUNT = 10;
    const MAX_FILE_SIZE_MB = 20;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    
    let removedFile: UploadedFile | undefined;
    
    // Remove the file
    setUploadedChatFiles(prev => {
      const fileToRemove = prev.find(file => file.id === fileId);
      removedFile = fileToRemove;
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(file => file.id !== fileId);
    });
    
    // Small delay to ensure state is updated
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Now check if we can retry error files - access fresh state
    setUploadedChatFiles(prev => {
      // Count current valid files
      const validFiles = prev.filter(
        f => f.status === 'success' || f.status === 'gettingurl' || f.status === 'inprogress'
      );
      
      const availableSlots = MAX_FILE_COUNT - validFiles.length;
      
      if (availableSlots <= 0) {
        return prev; // No slots available
      }
      
      // Build current file identifiers (excluding the removed file)
      const currentFileIdentifiers = new Set(
        prev.map(f => `${f.file.name}_${f.file.size}_${f.file.lastModified}`)
      );
      
      // Get error files that can be retried
      const countLimitErrors: UploadedFile[] = [];
      const duplicateErrors: UploadedFile[] = [];
      const seenDuplicateIdentifiers = new Set<string>();
      
      prev.forEach(f => {
        if (f.status !== 'error') return;
        if (f.file.size > MAX_FILE_SIZE_BYTES) return;
        
        // Collect count limit errors
        if (f.errorMessage === "Maximum 10 files allowed") {
          countLimitErrors.push(f);
        }
        // Collect duplicate errors (only if the removed file matches AND we haven't already added one for this identifier)
        else if (f.errorMessage === "Duplicate file" && removedFile) {
          const removedIdentifier = `${removedFile.file.name}_${removedFile.file.size}_${removedFile.file.lastModified}`;
          const errorIdentifier = `${f.file.name}_${f.file.size}_${f.file.lastModified}`;
          
          // Only add if this matches the removed file AND we haven't seen this identifier yet
          if (removedIdentifier === errorIdentifier && !seenDuplicateIdentifiers.has(errorIdentifier)) {
            duplicateErrors.push(f);
            seenDuplicateIdentifiers.add(errorIdentifier);
          }
        }
      });
      
      // Combine errors: prioritize duplicates (they're now valid since original is gone), then count limit errors
      const errorFilesToRetry = [...duplicateErrors, ...countLimitErrors].slice(0, availableSlots);
      
      if (errorFilesToRetry.length === 0) {
        return prev; // No files to retry
      }
      
      // Update files to gettingurl status and trigger uploads
      const updatedPrev = prev.map(f => {
        const shouldRetry = errorFilesToRetry.some(ef => ef.id === f.id);
        if (shouldRetry) {
          // Start upload asynchronously
          (async () => {
            try {
              const signedUrls = await getSignedUrl({
                file_names: [f.file.name],
                sec_ctx: 'tpra_chat'
              });
              
              if (signedUrls && signedUrls.length > 0) {
                const signedUrl = signedUrls[0];
                
                setUploadedChatFiles(prev2 => 
                  prev2.map(f2 => f2.id === f.id ? { ...f2, status: 'inprogress' as const, signedUrl } : f2)
                );
                
                setUploadProgress(prev2 => ({
                  ...prev2,
                  [f.file.name]: 0
                }));
                
                await uploadFileReq(signedUrl, f.file, (progressEvent: AxiosProgressEvent) => {
                  if (progressEvent.total) {
                    const progress = Math.round(
                      (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(prev2 => ({
                      ...prev2,
                      [f.file.name]: progress
                    }));
                  }
                });
                
                setUploadProgress(prev2 => {
                  const newProgress = { ...prev2 };
                  delete newProgress[f.file.name];
                  return newProgress;
                });
                
                setUploadedChatFiles(prev2 => 
                  prev2.map(f2 => f2.id === f.id ? 
                    { ...f2, status: 'success' as const, signedUrl } : f2
                  )
                );
              } else {
                throw new Error('Failed to get signed URL');
              }
            } catch (error) {
              console.error('File upload error:', error);
              
              setUploadProgress(prev2 => {
                const newProgress = { ...prev2 };
                delete newProgress[f.file.name];
                return newProgress;
              });
              
              setUploadedChatFiles(prev2 => 
                prev2.map(f2 => f2.id === f.id ? { ...f2, status: 'failed' as const } : f2)
              );
            }
          })();
          
          return { 
            ...f, 
            status: 'gettingurl' as const, 
            errorMessage: undefined,
            progressBar: true 
          };
        }
        return f;
      });
      
      return updatedPrev;
    });
  }, [getSignedUrl, uploadFileReq]);

  const handleAnsweredStatusChange = useCallback((allAnswered: boolean) => {
    setAreAllQuestionsAnswered(allAnswered);
  }, []);

  const handleOpenConfirmModal = useCallback(() => {
    // Always show confirmation modal before submission
    setIsConfirmModalOpen(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const handleConfirmSubmission = useCallback(() => {
    if (!token) {
      addNotification({
        message: "Token is missing. Please refresh the page.",
        type: "error",
      });
      return;
    }

    // Close modal immediately when user clicks "Yes, Confirm"
    setIsConfirmModalOpen(false);

    markCompleted(token, {
      onSuccess: (response) => {
        if (response.success) {
          // Navigate to AgentWaiting instead of showing notification
          onMarkCompleted?.();
        } else {
          addNotification({
            message: response.message || "Failed to mark assessment as complete.",
            type: "error",
          });
        }
      },
      onError: (error: any) => {
        console.error('Error marking questionnaire as completed:', error);
        addNotification({
          message: "Failed to mark assessment as complete. Please try again.",
          type: "error",
        });
      }
    });
  }, [token, markCompleted, onMarkCompleted]);

  return <ThirdPartyVendorLayout>
    <div className="h-full w-full flex flex-col">
      {/* Breadcrumb outside white background - matching questionnaire style */}
      <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px] px-0">
        <div className="flex gap-4 items-center">
          <ZBreadCrumbs
            items={[
              { title: "Third-Party Due Diligence", href: "" }
            ]}
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowHelpModal(true)}
            className="flex flex-row items-center gap-1.5 px-3 py-1 h-7 bg-white border border-[#006FEE] rounded-md transition-all hover:bg-[#F0F9FF]"
            style={{
              boxSizing: 'border-box',
              flex: 'none',
              order: 0,
              flexGrow: 0
            }}
          >
            <CircleHelp
              className="w-5 h-5 text-[#006FEE]"
              strokeWidth={1}
            />
          </button>

          {/* View Previous Responses Button */}
          {onViewPreviousResponses && (
            <ZButton
              variant="bordered"
              size="sm"
              onClick={onViewPreviousResponses}
              className="text-xs font-medium h-7 px-3 bg-white border border-[#006FEE] text-[#006FEE] hover:bg-[#F0F9FF]"
            >
              View Previous Responses
            </ZButton>
          )}

          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleOpenConfirmModal}
            isLoading={isMarkingCompleted}
            isDisabled={isMarkingCompleted}
          >
            Submit to Customer
          </ZButton>
        </div>
      </div>

      {/* Chat Interface - Full width like questionnaire */}
      <div className="flex-1 min-h-0">
        <SimpleTPEChat
          onFileUpload={handleChatFileUpload}
          uploadedFiles={uploadedChatFiles}
          uploadProgress={uploadProgress}
          onRemoveFile={handleRemoveChatFile}
          className="h-full"
          token={token}
          chatMessages={chatMessages}
          isLoadingMessages={isLoadingMessages}
          sessionId={sessionId}
          onMessageSent={onUserMessageSent}
          onAnsweredStatusChange={handleAnsweredStatusChange}
          onProgressChange={setProgressPercentage}
        />
      </div>
    </div>

    <SubmitConfirmationModal
      isOpen={isConfirmModalOpen}
      onClose={handleCloseConfirmModal}
      onConfirm={handleConfirmSubmission}
      isLoading={isMarkingCompleted}
      organizationName={assessorName}
    />

    <WelcomeModal
      isOpen={showHelpModal}
      onClose={() => setShowHelpModal(false)}
      mode="help"
      progressPercentage={100}
      currentStep={3}
    />

    <WelcomeModal
      isOpen={showChatWelcomeModal}
      onClose={() => setShowChatWelcomeModal(false)}
      mode="chat-welcome"
      progressPercentage={progressPercentage}
      currentStep={3}
    />
  </ThirdPartyVendorLayout>
}

export default SuccessScreen;